import React from "react";
import InternalError from "./500";
import NotFound from "./404";

const ErrorContext = React.createContext('');

class ErrorBoundary extends React.Component {
    static contextType = ErrorContext;

    constructor(props) {
        super(props);
        this.state = { hasError: false };
        this.errorHandler = this.onError.bind(this);
        this.rejectionHandler = this.onUnhandledRejection.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        if (error.message === "not-found") {
            this.setState({hasError: true, notFound: true});
        } else {
            this.setState({ hasError: true });
        }
    }

    componentDidMount() {
        window.addEventListener('error', this.errorHandler);
        window.addEventListener('unhandledrejection', this.rejectionHandler);
    }

    onError() {
        this.setState({ hasError: true });
    }

    onUnhandledRejection(event) {
        const { reason } = event;
        const isHttpError = Boolean(reason?.isAxiosError || reason?.response);
        if (!isHttpError) {
            this.setState({ hasError: true });
            return;
        }
        if (reason.response?.status === 403 && reason.config?.url === "/v1/refresh-token") {
            localStorage.clear();
            window.location.href = "/";
            return;
        }
        // Failed requests leave the rendered UI intact; the calling page owns their error state.
        console.error("Unhandled request failure:", reason);
    }

    componentWillUnmount() {
        window.removeEventListener('error', this.errorHandler);
        window.removeEventListener('unhandledrejection', this.rejectionHandler);
    }

    render() {
        const { notFound, hasError } = this.state;
        if (hasError) {
            return (
                <ErrorContext.Provider value={{setError: this.errorHandler, resetError: () => this.setState({hasError: false})}}>
                    <div className="flex min-h-screen bg-canvas text-fg">
                        {notFound ? <NotFound showLogo /> : <InternalError showLogo />}
                    </div>
                </ErrorContext.Provider>
            );
        }

        return <>{this.props.children}</>;
    }
}

export {ErrorBoundary, ErrorContext};